import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  ExternalLink,
  Check,
  Loader2,
  Eye,
  EyeOff,
  Search,
  X,
} from 'lucide-react'
import { useCan } from '@/features/auth'
import { parseCategories } from '@/features/admin/newsCategories'
import { ImageField } from '@/features/admin/components/ImageField'
import { Markdown } from '@/shared/components/markdown/Markdown'
import { MarkdownEditor } from '@/shared/components/markdown/MarkdownEditor'
import { Pagination } from '@/shared/components/ui/pagination'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { youtubeThumbnail } from '@/shared/utils/youtube'
import {
  fetchAdminNews,
  createNews,
  updateNews,
  deleteNews,
  type NewsEntry,
  type NewsInput,
  type AdminNewsResult,
} from '@/features/news/news'

const PAGE_SIZE = 8
type StatusFilter = 'all' | 'published' | 'draft'

const emptyEntry = (): NewsEntry => ({
  id: '',
  title: '',
  date: '',
  categories: [],
  image: '',
  youtubeUrl: '',
  excerpt: '',
  body: '',
  published: false,
})

export function NewsAdminPage() {
  const canEdit = useCan('landing:edit')
  const [draft, setDraft] = useState<NewsEntry | null>(null)

  // Estado del listado (server-side)
  const [data, setData] = useState<AdminNewsResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [category, setCategory] = useState('all')
  const [page, setPage] = useState(1)
  const [saving, setSaving] = useState(false)

  // Debounce del buscador
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300)
    return () => clearTimeout(t)
  }, [query])

  // Reiniciar a página 1 al cambiar búsqueda/filtros
  useEffect(() => {
    setPage(1)
  }, [debounced, status, category])

  // Cargar listado
  useEffect(() => {
    if (!canEdit || draft) return
    let active = true
    setLoading(true)
    fetchAdminNews({ page, pageSize: PAGE_SIZE, q: debounced, status, category })
      .then((d) => {
        if (!active) return
        // Si la página quedó vacía tras borrar y no es la primera, retroceder.
        if (d.items.length === 0 && d.total > 0 && page > 1) {
          setPage((p) => Math.max(1, p - 1))
        } else {
          setData(d)
        }
      })
      .catch(() => active && toast.error('No se pudieron cargar las entradas'))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [canEdit, draft, page, debounced, status, category, reloadKey])

  if (!canEdit) {
    return (
      <div className="rounded-card border border-line bg-panel p-8 text-center">
        <h2 className="text-lg font-semibold text-ink">Acceso restringido</h2>
        <p className="mt-1 text-sm text-ink-muted">Esta sección es exclusiva del administrador del portal.</p>
      </div>
    )
  }

  const reload = () => setReloadKey((k) => k + 1)

  const saveDraft = async () => {
    if (!draft) return
    setSaving(true)
    const input: NewsInput = {
      title: draft.title,
      date: draft.date,
      categories: draft.categories,
      image: draft.image,
      youtubeUrl: draft.youtubeUrl,
      excerpt: draft.excerpt,
      body: draft.body,
      published: draft.published,
    }
    try {
      if (draft.id) await updateNews(draft.id, input)
      else await createNews(input)
      setDraft(null)
      reload()
    } catch {
      toast.error('No se pudo guardar la entrada')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: string) => {
    try {
      await deleteNews(id)
      reload()
    } catch {
      toast.error('No se pudo eliminar la entrada')
    }
  }

  if (draft) {
    return (
      <NewsEditor
        draft={draft}
        setDraft={setDraft}
        onSave={saveDraft}
        onCancel={() => setDraft(null)}
        saving={saving}
        isNew={!draft.id}
      />
    )
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const categories = data?.categories ?? []
  const hasFilters = query.trim() !== '' || status !== 'all' || category !== 'all'

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-widest text-brand">Administración del portal</span>
          <h1 className="mt-1 font-serif text-[32px] font-normal leading-[1.1] tracking-tight text-ink">Lo último</h1>
          <p className="mt-1 text-sm text-ink-muted">Gestiona las novedades, casos de éxito y consejos del sitio.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/lo-ultimo" target="_blank" className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-panel px-3 py-2 text-sm font-medium text-ink hover:bg-surface">
            <ExternalLink className="size-4" /> Ver sitio
          </Link>
          <button onClick={() => setDraft(emptyEntry())} className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark">
            <Plus className="size-4" /> Nueva entrada
          </button>
        </div>
      </header>

      {/* Buscador + filtros */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por título, texto o categoría…"
            className="w-full rounded-lg border border-line bg-white py-2 pl-9 pr-9 text-sm text-ink outline-none focus:border-brand"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded text-ink-subtle hover:text-ink">
              <X className="size-3.5" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-line bg-panel p-0.5">
            {([['all', 'Todas'], ['published', 'Publicadas'], ['draft', 'Borradores']] as const).map(([val, label]) => (
              <button
                key={val}
                onClick={() => setStatus(val)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  status === val ? 'bg-brand text-white' : 'text-ink-muted hover:text-ink'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {(categories.length > 0 || category !== 'all') && (
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand"
            >
              <option value="all">Todas las categorías</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-card border border-line bg-panel py-16 text-ink-muted">
          <Loader2 className="mr-2 size-5 animate-spin" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-card border border-dashed border-line bg-panel p-10 text-center">
          <p className="text-sm text-ink-muted">
            {total === 0 && !hasFilters
              ? 'Aún no hay entradas. Crea la primera con "Nueva entrada".'
              : 'No hay entradas que coincidan con la búsqueda o los filtros.'}
          </p>
        </div>
      ) : (
        <>
          <ul className="space-y-2">
            {items.map((n) => {
              const cover = n.image || youtubeThumbnail(n.youtubeUrl)
              return (
                <li key={n.id} className="flex items-center gap-4 rounded-card border border-line bg-panel p-3">
                  <div className="h-14 w-20 shrink-0 overflow-hidden rounded-lg bg-surface">
                    {cover ? (
                      <img src={cover} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="grid h-full w-full place-items-center text-[10px] text-ink-subtle">sin imagen</div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate text-sm font-semibold text-ink">{n.title || 'Sin título'}</h3>
                      {n.published ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-1.5 py-0.5 text-[9px] font-bold uppercase text-success"><Eye className="size-2.5" /> Publicada</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-surface px-1.5 py-0.5 text-[9px] font-bold uppercase text-ink-subtle"><EyeOff className="size-2.5" /> Borrador</span>
                      )}
                    </div>
                    <p className="truncate text-xs text-ink-muted">
                      {n.date}
                      {n.categories.length > 0 && ` · ${n.categories.join(', ')}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button onClick={() => setDraft({ ...n })} className="grid size-8 place-items-center rounded-lg text-ink-muted hover:bg-surface hover:text-ink" title="Editar">
                      <Pencil className="size-4" />
                    </button>
                    <button onClick={() => void remove(n.id)} className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-danger-soft hover:text-danger" title="Eliminar">
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>

          <Pagination page={page} pageSize={PAGE_SIZE} totalItems={total} onPageChange={setPage} />
        </>
      )}
    </div>
  )
}

/* ------------------------------ EDITOR ------------------------------ */

function NewsEditor({
  draft,
  setDraft,
  onSave,
  onCancel,
  saving,
  isNew,
}: {
  draft: NewsEntry
  setDraft: (n: NewsEntry) => void
  onSave: () => void
  onCancel: () => void
  saving: boolean
  isNew: boolean
}) {
  const [preview, setPreview] = useState(false)
  // El texto de categorías se controla APARTE del draft: si el input se
  // controlara con draft.categories.join(', '), el filter(Boolean) del parseo
  // se comería la coma recién tecleada ("Consejos," → ['Consejos'] → re-render
  // sin coma) y sería imposible escribir más de una categoría.
  const [categoriesText, setCategoriesText] = useState(() => draft.categories.join(', '))
  const patch = (p: Partial<NewsEntry>) => setDraft({ ...draft, ...p })

  return (
    <div className="space-y-5 pb-24">
      <button onClick={onCancel} className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-muted hover:text-ink">
        <ArrowLeft className="size-4" /> Todas las entradas
      </button>

      <h1 className="font-serif text-[28px] font-normal tracking-tight text-ink">{isNew ? 'Nueva entrada' : 'Editar entrada'}</h1>

      {/* Metadatos */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="Título" value={draft.title} onChange={(v) => patch({ title: v })} />
        </div>
        <Field label="Fecha (texto, ej. Mar 2026)" value={draft.date} onChange={(v) => patch({ date: v })} />
        <Field
          label="Categorías (separadas por coma)"
          value={categoriesText}
          onChange={(v) => {
            setCategoriesText(v)
            patch({ categories: parseCategories(v) })
          }}
        />
        <div className="sm:col-span-2">
          <Field label="Resumen (para las tarjetas)" value={draft.excerpt} onChange={(v) => patch({ excerpt: v })} textarea />
        </div>
        <div className="sm:col-span-2">
          <ImageField label="Imagen de portada" value={draft.image} aspect="aspect-[16/10]" onChange={(v) => patch({ image: v })} />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-ink">
        <input type="checkbox" checked={draft.published} onChange={(e) => patch({ published: e.target.checked })} className="size-4 accent-[color:var(--brand-orange)]" />
        Publicada (visible en el sitio)
      </label>

      {/* Cuerpo a pantalla completa */}
      <div>
        <label className="mb-1.5 block text-xs font-semibold text-ink-muted">Cuerpo (Markdown)</label>
        <MarkdownEditor
          value={draft.body}
          onChange={(v) => patch({ body: v })}
          heightClass="h-[460px]"
          toolbarExtra={
            <button onClick={() => setPreview(true)} className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold text-brand hover:bg-surface">
              <Eye className="size-3.5" /> Visualizar
            </button>
          }
        />
        <p className="mt-2 text-[11px] text-ink-subtle">
          Sube imágenes con el botón <strong>Imagen</strong> o videos con <strong>Video</strong> (pega el enlace de YouTube). También puedes escribir <code className="rounded bg-surface px-1">![texto](URL)</code> a mano.
        </p>
      </div>

      {/* Barra de guardado */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-panel/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          <button onClick={onCancel} className="text-sm font-medium text-ink-muted hover:text-ink">Cancelar</button>
          <button
            onClick={onSave}
            disabled={saving || !draft.title.trim()}
            className="ml-auto inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
          >
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            {saving ? 'Guardando…' : 'Guardar entrada'}
          </button>
        </div>
      </div>

      {/* Modal de vista previa */}
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Vista previa</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <article>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                {draft.categories.map((c) => (
                  <span key={c} className="rounded-full bg-brand-soft px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-brand">{c}</span>
                ))}
                {draft.date && <span className="text-sm text-ink-muted">{draft.date}</span>}
              </div>
              <h2 className="font-serif text-2xl font-normal tracking-tight text-ink">{draft.title || 'Sin título'}</h2>
              {draft.image && (
                <img src={draft.image} alt={draft.title} className="mt-5 w-full rounded-2xl border border-line object-cover" />
              )}
              <div className="mt-5">
                {draft.body.trim() ? <Markdown>{draft.body}</Markdown> : <p className="text-sm text-ink-subtle">Sin contenido.</p>}
              </div>
            </article>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Field({ label, value, onChange, textarea }: { label: string; value: string; onChange: (v: string) => void; textarea?: boolean }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-ink-muted">{label}</span>
      {textarea ? (
        <textarea value={value} rows={2} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full resize-y rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand" />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand" />
      )}
    </label>
  )
}
